"use client";

import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslations } from "next-intl";
import { useRoomListing } from "@/components/modules/listings/api/queries/useRoomListing";
import { SHARED_FIELD, BOOL_SHARED_FIELDS } from "@/components/modules/listings/ListingDetail/components/sharedFieldCompare";
import { associationSharedFields, type AssociationFieldKey, type PropertyShared } from "../sharedFieldsToApply";
export type FieldDiffRow = {
  key: AssociationFieldKey;
  label: string;
  from: string;
  to: string;
  fromPills?: string[];
  toPills?: string[];
};
export function useAssociateFieldsStep({
  property,
  listingId
}: {
  property?: PropertyShared | null;
  listingId?: string;
}) {
  const t = useTranslations("modules.portfolio.associateListingToUnit.stepper");
  const tFeatures = useTranslations("modules.listings.addRoomListing.features");
  const {
    setValue
  } = useFormContext();
  const {
    data
  } = useRoomListing({
    listingId: listingId ?? ""
  });
  const listing = data?.data;
  const changedKeys = associationSharedFields(property, listing).filter(f => f.differs).map(f => f.key);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const selected = changedKeys.filter(key => overrides[key] ?? true);
  const selectedKey = selected.join(",");
  useEffect(() => {
    setValue("fieldsToApply", selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);
  const scalar = (v: unknown) => v == null || v === "" ? "—" : String(v);
  const featureLabels = (keys?: string[]) => keys && keys.length > 0 ? keys.map(k => tFeatures(k)) : [];
  const rows: FieldDiffRow[] = changedKeys.map(key => {
    if (key === SHARED_FIELD.houseFeatures) return {
      key,
      label: t("fieldHouseFeatures"),
      from: "",
      to: "",
      fromPills: featureLabels(listing?.houseFeatures),
      toPills: featureLabels(property?.houseFeatures)
    };
    if (key === SHARED_FIELD.location) return {
      key,
      label: t("fieldLocation"),
      from: scalar(listing?.locationNormalizedName),
      to: scalar(property?.locationNormalizedName)
    };
    if (BOOL_SHARED_FIELDS.includes(key)) {
      const boolLabel = (v: unknown) => v ? t("valueYes") : t("valueNo");
      const label = key === SHARED_FIELD.petFriendly ? t("fieldPetFriendly") : key === SHARED_FIELD.contract ? t("fieldContract") : t("fieldSmokingAllowed");
      return {
        key,
        label,
        from: boolLabel(listing?.[key]),
        to: boolLabel(property?.[key])
      };
    }
    return {
      key,
      label: key === SHARED_FIELD.bedrooms ? t("fieldBedrooms") : t("fieldBathrooms"),
      from: scalar(listing?.[key]),
      to: scalar(property?.[key])
    };
  });
  const toggle = (key: string) => setOverrides(prev => ({
    ...prev,
    [key]: !(prev[key] ?? true)
  }));
  return {
    rows,
    selected,
    toggle
  };
}